import { flexRender } from "@tanstack/react-table";
import { useTranslate } from "@tolgee/react";
import { ArrowDown, ArrowUp, Search, Star, X } from "lucide-react";
import { cloneElement, isValidElement, type ReactNode } from "react";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "#/components/ui/popover.tsx";
import { KeyboardHintsBar } from "./keyboard-hints-bar.tsx";
import {
	CATEGORY_TABS,
	usePerpMarketSearch,
} from "./use-perp-market-search.ts";

const MARKET_GRID_CLASS =
	"grid grid-cols-[20px_minmax(0,2.35fr)_minmax(60px,1fr)_minmax(60px,1fr)] md:grid-cols-[20px_minmax(0,2.6fr)_minmax(72px,1fr)_minmax(72px,1fr)_minmax(72px,1fr)_minmax(88px,1fr)_minmax(100px,1.1fr)] gap-2";

const HIDDEN_ON_MOBILE = new Set([
	"predictedFundingRate",
	"dayNotionalVolume",
	"openInterestValue",
]);

type PerpMarketSearchDialogProps = {
	currentCoin: string;
	trigger: ReactNode;
};

export function PerpMarketSearchDialog({
	currentCoin,
	trigger,
}: PerpMarketSearchDialogProps) {
	const { t } = useTranslate();
	const search = usePerpMarketSearch();

	const triggerNode = isValidElement<{ className?: string }>(trigger)
		? cloneElement(trigger, {
				className: [
					trigger.props.className,
					"[&_[data-slot=popover-trigger-icon]]:transition-transform",
					"[&_[data-slot=popover-trigger-icon]]:duration-200",
					"[&_[data-slot=popover-trigger-icon]]:ease-out",
					"data-[state=open]:[&_[data-slot=popover-trigger-icon]]:rotate-180",
				]
					.filter(Boolean)
					.join(" "),
			})
		: trigger;

	return (
		<Popover open={search.open} onOpenChange={search.setOpen}>
			<PopoverTrigger asChild>{triggerNode}</PopoverTrigger>
			<PopoverContent
				side="bottom"
				align="start"
				className="w-[calc(100vw-1.5rem)] overflow-hidden rounded-md border border-border bg-card p-0 dark:border-[var(--color-theme-line-default)] dark:bg-[var(--color-theme-bg-elevated)] sm:-ml-8 sm:w-[min(960px,calc(100vw-2rem))]"
				onKeyDown={search.handleContentKeyDown}
			>
				<div className="flex items-center gap-2 border-b border-trade-border-subtle px-3 py-1.5">
					<Search className="h-3.5 w-3.5 shrink-0 text-trade-text-secondary" />
					<input
						ref={search.inputRef}
						type="text"
						value={search.query}
						onChange={(event) => search.setQuery(event.target.value)}
						placeholder={t("market-search.placeholder")}
						className="h-7 flex-1 border-0 bg-transparent text-label1 text-trade-text-strong outline-none placeholder:text-trade-text-faint"
					/>
					<button
						type="button"
						onClick={() => search.setOpen(false)}
						className="inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-trade-text-secondary transition-[color,background-color] hover:bg-trade-interactive hover:text-trade-text-strong"
						aria-label={t("market-search.close-aria")}
					>
						<X className="h-4 w-4" />
					</button>
				</div>

				<div className="flex items-center gap-3 border-b border-trade-border-subtle px-3">
					{CATEGORY_TABS.map((tab) => (
						<button
							key={tab.key}
							type="button"
							onClick={() => search.setCategory(tab.key)}
							className={`border-b-2 py-1.5 text-caption1-medium transition-colors cursor-pointer ${
								search.category === tab.key
									? "border-primary text-trade-text-strong"
									: "border-transparent text-trade-text-faint hover:text-trade-text-secondary"
							}`}
						>
							{t(tab.labelKey)}
							{tab.key === "favorites" && search.favorites.length > 0
								? ` (${search.favorites.length})`
								: ""}
						</button>
					))}
				</div>

				<div
					className={`${MARKET_GRID_CLASS} border-b border-trade-border-subtle px-3 py-1.5 text-caption1-medium uppercase text-trade-text-faint`}
				>
					<div />
					{search.table.getHeaderGroups().map((headerGroup) =>
						headerGroup.headers.map((header) => {
							const canSort = header.column.getCanSort();
							const sortDir = header.column.getIsSorted();
							const isFirst = header.index === 0;

							return (
								<button
									key={header.id}
									type="button"
									onClick={header.column.getToggleSortingHandler()}
									disabled={!canSort}
									className={`${HIDDEN_ON_MOBILE.has(header.id) ? "hidden md:flex" : "flex"} items-center gap-1 ${isFirst ? "text-left" : "justify-end text-right"} ${canSort ? "cursor-pointer select-none hover:text-trade-text-secondary" : "cursor-default"}`}
								>
									{flexRender(
										header.column.columnDef.header,
										header.getContext(),
									)}
									{sortDir === "asc" ? (
										<ArrowUp className="h-3 w-3" />
									) : sortDir === "desc" ? (
										<ArrowDown className="h-3 w-3" />
									) : null}
								</button>
							);
						}),
					)}
				</div>

				<div
					ref={search.setScrollElement}
					className="max-h-[min(50vh,480px)] overflow-y-auto"
				>
					{search.isLoading ? (
						<div className="px-3 py-6 text-label1 text-trade-text-secondary">
							{t("market-search.loading")}
						</div>
					) : search.isError ? (
						<div className="px-3 py-6 text-label1 text-trade-red">
							{t("market-search.error")}
						</div>
					) : search.rows.length === 0 ? (
						<div className="flex flex-col items-start gap-2 px-3 py-6 text-label1 text-trade-text-secondary">
							<span>{search.emptyMessage}</span>
							{search.showCrossCategoryHint && (
								<button
									type="button"
									onClick={() => search.setCategory("all")}
									className="cursor-pointer text-primary hover:underline"
								>
									{t("market-search.show-all-with-count", {
										count: search.totalQueryMatchCount,
									})}
								</button>
							)}
						</div>
					) : (
						<div
							style={{
								height: search.virtualizer.getTotalSize(),
								position: "relative",
							}}
						>
							{search.virtualizer.getVirtualItems().map((virtualRow) => {
								const row = search.rows[virtualRow.index];
								const market = row.original;
								const isHighlighted =
									virtualRow.index === search.highlightedIndex;
								const isActive = market.coin === currentCoin;

								return (
									<div
										key={row.id}
										style={{
											position: "absolute",
											top: 0,
											left: 0,
											right: 0,
											transform: `translateY(${virtualRow.start}px)`,
											height: virtualRow.size,
										}}
									>
										<div
											className={`${MARKET_GRID_CLASS} h-full w-full items-center border-b border-trade-border-subtle/40 px-3 py-0.5 text-left transition hover:bg-trade-interactive-hover dark:hover:bg-[var(--color-theme-fill-strong)] ${
												isHighlighted
													? "bg-trade-interactive"
													: isActive
														? "bg-trade-surface-raised"
														: ""
											}`}
										>
											<button
												type="button"
												onClick={(e) => {
													e.stopPropagation();
													search.toggleFavorite(market.coin);
												}}
												className="flex h-full items-center justify-center"
												aria-label={
													search.favoritesSet.has(market.coin)
														? t("market-search.remove-favorite")
														: t("market-search.add-favorite")
												}
											>
												<Star
													className={`h-3 w-3 transition-colors ${
														search.favoritesSet.has(market.coin)
															? "fill-[var(--color-theme-denotives-yellow)] text-[var(--color-theme-denotives-yellow)]"
															: "text-trade-text-faint hover:text-trade-text-secondary"
													}`}
												/>
											</button>
											{row.getVisibleCells().map((cell, index) => (
												<button
													key={cell.id}
													type="button"
													onClick={() =>
														search.handleSelect(market.routeSymbol)
													}
													className={`${HIDDEN_ON_MOBILE.has(cell.column.id) ? "hidden md:block" : ""} cursor-pointer ${index > 0 ? "text-right" : "text-left"}`}
												>
													{flexRender(
														cell.column.columnDef.cell,
														cell.getContext(),
													)}
												</button>
											))}
										</div>
									</div>
								);
							})}
						</div>
					)}
				</div>

				<KeyboardHintsBar />
			</PopoverContent>
		</Popover>
	);
}
